import { useField, useFormikContext } from "formik";
import {
	TimePickerWithLabel,
	parseTime,
	time12To24,
} from "newui/components/ui/InputWithLabel/TimePickerWithLabel";

/**
 * Timezone options for RFx submission deadlines. Values are the abbreviations
 * stored in `submission_deadline_timezone` (and understood by the backend,
 * emails, reports, and `timezoneChecker`), so no IANA mapping is needed.
 */
export const RFX_TIMEZONE_OPTIONS = [
	{ value: "AWST", label: "AWST (Perth)" },
	{ value: "AEST", label: "AEST (Sydney)" },
];

/**
 * Convert the picker's 12-hour "hh:mm A" output to the 24-hour "HH:mm" the
 * form and deadline serialisation expect. Exported for testing.
 */
export const to24HourTime = (time: string): string => {
	const { hour, minute } = time12To24(parseTime(time));
	return `${hour}:${minute}`;
};

interface SubmissionDeadlineTimePickerProps {
	name: string;
	nameTimezone: string;
	label?: string;
	required?: boolean;
	className?: string;
}

/**
 * Formik-bound wrapper around the newui TimePickerWithLabel for the RFx
 * submission deadline. The picker emits 12-hour "hh:mm A", but the form (and
 * the deadline serialisation) works in 24-hour "HH:mm", so the value is
 * converted at this boundary. Timezone selection is constrained to the
 * abbreviations the rest of the app supports.
 */
export const SubmissionDeadlineTimePicker = ({
	name,
	nameTimezone,
	label,
	required,
	className,
}: SubmissionDeadlineTimePickerProps) => {
	const [timeField, timeMeta] = useField<string>(name);
	const [timezoneField] = useField<string>(nameTimezone);
	const { setFieldValue } = useFormikContext();

	const handleTimeChange = (time: string) => {
		setFieldValue(name, to24HourTime(time));
	};

	const handleTimeZoneChange = (timezone: string) => {
		setFieldValue(nameTimezone, timezone);
	};

	return (
		<TimePickerWithLabel
			name={name}
			nameTimezone={nameTimezone}
			label={label}
			required={required}
			className={className}
			value={timeField.value}
			timezone={timezoneField.value}
			timezoneOptions={RFX_TIMEZONE_OPTIONS}
			hasError={!!(timeMeta.error && timeMeta.touched)}
			errorText={timeMeta.touched ? timeMeta.error : undefined}
			onTimeChange={handleTimeChange}
			onTimeZoneChange={handleTimeZoneChange}
		/>
	);
};
